import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaThLarge, FaUserCircle, FaSignOutAlt, FaUser, FaCog } from 'react-icons/fa';
import ThemeToggle from '../ThemeToggle';
import { UNAUTH_ADMIN_REDIRECT } from '../../helper/authPaths';

const ROUTE_TITLES = {
    dashboard: 'Dashboard',
    loa: 'Data LOA',
    journal: 'Journal',
    users: 'Users',
    payment: 'Transaksi',
    'payment-method': 'Metode Pembayaran',
    menus: 'Manajemen Menu',
    'menu-access': 'Akses Menu',
    'website-settings': 'Setting Website',
    settings: 'Pengaturan Akun',
};

function getPageTitle(pathname) {
    if (pathname === '/admin' || pathname === '/admin/') {
        return 'Dashboard';
    }

    const parts = pathname.replace(/^\/admin\/?/, '').split('/').filter(Boolean);
    const root = parts[0] || 'dashboard';

    if (parts.length === 1) {
        return ROUTE_TITLES[root] || root.replace(/-/g, ' ');
    }

    if (root === 'loa') {
        if (parts[1] === 'create') return 'Tambah LOA';
        if (parts[2] === 'edit') return 'Edit LOA';
        return 'Detail LOA';
    }

    if (root === 'users') {
        if (parts[1] === 'create') return 'Tambah User';
        if (parts[2] === 'edit') return 'Edit User';
    }

    if (root === 'journal' && parts[2] === 'issues') {
        return 'Manage Issues';
    }

    if (root === 'payment') {
        if (parts[1] === 'bill') return 'Payment Bill';
        if (parts[1] === 'invoice') return 'Invoice';
        if (parts[1]) return 'Detail Payment';
    }

    return ROUTE_TITLES[root] || root.replace(/-/g, ' ');
}

export default function NavbarAdmin({ showSidebar, setShowSidebar, isCollapsed, setIsCollapsed, data }) {
    const location = useLocation().pathname;
    const [flyer, setFlyer] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);
    const nav = useNavigate();

    const pageTitle = getPageTitle(location);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                flyer &&
                dropdownRef.current &&
                buttonRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRef.current.contains(event.target)
            ) {
                setFlyer(false);
            }
        };

        if (flyer) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [flyer]);

    const logout = async () => {
        try {
            await axios.post('api/auth/logout', {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            window.location.href = UNAUTH_ADMIN_REDIRECT;
        }
    };

    const iconBtn =
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-xs text-muted transition-colors hover:bg-surface-soft hover:text-ink dark:hover:bg-white/5 dark:hover:text-white';

    return (
        <nav className="h-14 w-full border-b border-hairline bg-canvas dark:border-white/10 dark:bg-[#1f2128]">
            <div className="flex h-full items-center justify-between gap-2 px-3 sm:gap-4 sm:px-4 md:px-6">
                <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
                    <button
                        type="button"
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className={`hidden ${iconBtn} md:flex`}
                        aria-label="Toggle sidebar"
                    >
                        <FaThLarge className="text-xs" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowSidebar(showSidebar === 'left-0' ? '-left-72' : 'left-0')}
                        className={`${iconBtn} md:hidden`}
                        aria-label="Toggle sidebar"
                    >
                        <FaThLarge className="text-xs" />
                    </button>
                    <h1 className="truncate text-sm font-medium text-ink dark:text-white sm:text-[15px]">
                        {pageTitle}
                    </h1>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                    <ThemeToggle />
                    <div className="relative">
                        <button
                            ref={buttonRef}
                            type="button"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xs border border-hairline bg-surface-soft text-body transition-colors hover:border-border-strong dark:border-white/10 dark:bg-white/5 dark:text-white sm:w-auto sm:max-w-[200px] sm:justify-start sm:gap-2 sm:px-3"
                            onClick={() => setFlyer(!flyer)}
                            aria-label="Menu pengguna"
                        >
                            <FaUserCircle className="shrink-0 text-accent-brand dark:text-accent-brand" />
                            <span className="hidden truncate sm:inline">{data.user.nama}</span>
                        </button>
                        {flyer && (
                            <div
                                ref={dropdownRef}
                                className="absolute right-0 z-20 mt-1.5 w-56 overflow-hidden rounded-md border border-hairline bg-canvas dark:border-white/10 dark:bg-tile-1"
                            >
                                <div className="border-b border-hairline px-4 py-3 dark:border-white/10">
                                    <p className="truncate text-sm font-medium text-ink dark:text-white">
                                        {data.user.nama}
                                    </p>
                                    <p className="text-xs capitalize text-muted">{data.user.role}</p>
                                </div>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-body hover:bg-surface-soft dark:text-white dark:hover:bg-white/5"
                                    onClick={() => {
                                        setFlyer(false);
                                        nav('/admin/website-settings');
                                    }}
                                >
                                    <FaCog className="text-xs text-muted" />
                                    Setting Website
                                </button>
                                <button
                                    type="button"
                                    className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-body hover:bg-surface-soft dark:text-white dark:hover:bg-white/5"
                                    onClick={() => {
                                        setFlyer(false);
                                        nav('/admin/settings');
                                    }}
                                >
                                    <FaUser className="text-xs text-muted" />
                                    Pengaturan Akun
                                </button>
                                <button
                                    type="button"
                                    onClick={logout}
                                    className="flex w-full items-center gap-2.5 border-t border-hairline px-4 py-2.5 text-sm text-muted hover:bg-surface-soft hover:text-ink dark:border-white/10 dark:hover:bg-white/5"
                                >
                                    <FaSignOutAlt className="text-xs" />
                                    Log Out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}
